import { createReducer, on, Action, Store } from '@ngrx/store';
import { System } from 'ngx-core-services';
import { isEqual } from 'lodash';

import { initialSystemState, SystemDefinition, SystemsState } from '../state/system.state';
import {
  removeSystem,
  updateSystem,
  setSystemSubscribed,
  setSystemUnsubscribed,
  addSystem,
  changeTemporarySystemState,
  systemSelection,
  changeMetadataLoadingState,
  updateCurrentObservers,
  requestToAddASystem,
  updateVirtualRTStatus,
} from '../actions/system.actions';
import { updateConnextData } from '../actions/connext.actions';
import {
  addSystem as addSystemOperation,
  requestToAddASystem as requestToAddASystemOperation,
  removeSystem as removeSystemOperation,
  updateSystem as updateSystemOperation,
  updateCurrentObservers as updateCurrentObserversOperation,
  setSystemSubscribed as setSystemSubscribedOperation,
  setSystemUnsubscribed as setSystemUnsubscribedOperation,
  updateConnextData as updateConnextDataOperation,
  updateAzzurroHubData as updateAzzurroHubDataOperation
} from './system.operations';
import { isSystemEqualToSystemDefinition } from '..';
import { updateAzzurroHubData } from '../actions/azzurroHub.actions';

const _systemReducer = createReducer(
  initialSystemState,
  on(requestToAddASystem, (state, { systemId, observerKey }) => requestToAddASystemOperation(state, systemId, observerKey)),
  on(addSystem, (state, { systemId, system }) => addSystemOperation(state, systemId, system)),
  on(systemSelection, (state, { systemId }) => {
    return {
      ...state,
      selectedSystemUid: systemId,
    };
  }),
  on(changeTemporarySystemState, (appState, { systemId, state }) => {
    return {
      ...appState,
      temporarySystemState: {
        ...appState.temporarySystemState,
        [systemId]: state,
      },
    };
  }),
  on(removeSystem, (state, { systemId }) => removeSystemOperation(state, systemId)),
  on(updateSystem, (state, { systemId, changes }) => updateSystemOperation(state, systemId, changes)),
  on(updateConnextData, (state, { systemId, changes }) => updateConnextDataOperation(state, systemId, changes)),
  on(updateAzzurroHubData, (state, { systemId, changes }) => updateAzzurroHubDataOperation(state, systemId, changes)),
  on(updateCurrentObservers, (state, { systemId, observerKey, operation }) => updateCurrentObserversOperation(state, systemId, observerKey, operation)),
  on(setSystemSubscribed, (state, { systemId }) => setSystemSubscribedOperation(state, systemId)),
  on(setSystemUnsubscribed, (state, { systemId }) => setSystemUnsubscribedOperation(state, systemId)),
  on(changeMetadataLoadingState, (state, { systemId, loading }) => {
    const system = state.systemList[systemId];
    if (!system) {
      return state;
    }
    return {
      ...state,
      systemList: {
        ...state.systemList,
        [systemId]: {
          ...system,
          isMetadataLoading: loading,
        },
      },
    };
  }),
  on(updateVirtualRTStatus, (state, { systemId, virtualRTStatus }) => {
    const system = state.systemList[systemId];
    if (!system) {
      return state;
    }
    return {
      ...state,
      systemList: {
        ...state.systemList,
        [systemId]: {
          ...system,
          virtualRTStatus
        },
      },
    };
  }),
);

export function systemReducer(state: SystemsState | undefined, action: Action) {
  return _systemReducer(state, action);
}

export const checkAndUpdateSystemIfNecessary = (system: System, systemDefinition: SystemDefinition, store: Store) => {
  if (!system.uid){
    return;
  }

  if (!isSystemEqualToSystemDefinition(system, systemDefinition)) {
    store.dispatch(updateSystem({ systemId: system.uid, changes: systemDefinition }));
  }
};

export const checkAndUpdateSystemDefinitionIfNecessary = (storeSystemDefinition: System, newSystemDefinition: SystemDefinition, store: Store) => {
  if (!storeSystemDefinition.uid){
    return;
  }

  if (!isEqual(storeSystemDefinition, newSystemDefinition)) {
    store.dispatch(updateSystem({ systemId: storeSystemDefinition.uid, changes: newSystemDefinition }));
  }
}

export const checkAndUpdateSystemDefinitionIfNecessaryWithSystem = (storeSystem: SystemDefinition, newSystemDefinition: System, store: Store) => {
  if (!storeSystem.uid){
    return;
  }

  /**
   * Check if the newSystemDefinition is different from the storeSystem
   * for each attribute of newSystemDefinition, check if storeSystem has the same value and if not update
   */
  for (const key in newSystemDefinition) {
    const storeSystemValue = storeSystem[key as keyof SystemDefinition];
    const newSystemValue = newSystemDefinition[key as keyof System];

    if (!isEqual(storeSystemValue, newSystemValue)) {
      store.dispatch(updateSystem({ systemId: storeSystem.uid, changes: newSystemDefinition }));
      break;
    }
  }
}
